import {
  Div,
  Flex,
  FormStatus,
  HelpButton,
  Icon,
  IconPrimary,
  P,
  Section,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@dnb/eufemia';
import { Field, Form, Value } from '@dnb/eufemia/extensions/forms';
import { exclamation_triangle } from '@dnb/eufemia/icons';
import { hasRequiredServiceNowInformation } from '@portals/shared/common/utils/apiUtils';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { updateAccessManagement } from '@/api/api';
import { useBackend } from '@/hooks/useBackend';
import { ApiAccessManagementRequest } from '@/openapi/schema';
import { ApiProviderPageOutletContext } from '@/pages/profile/apps/application/ApiProviderPage';
import FormSubmitButton from '@/pages/profile/apps/application/ApiProviderPage/components/FormSubmitButton';
import { additionalSettingsHelpContent } from '@/pages/profile/apps/application/ApiProviderPage/VisibilityAndAccessManagementTab/AccessManagementSection/AdditionalSettingsHelpContent';
import { SwitchContainerMode } from '@/types/SwitchContainerModeTypes';
import { apiOrEvent } from '@/utils/integrationServiceUtils';

import { FormEditButton } from '../../components/FormEditButton';

import style from './index.module.css';

enum ApprovalType {
  AUTOMATIC = 'Automatic',
  SERVICE_NOW = 'ServiceNow',
}

function AccessManagementSettingsTable({ children }: { children: React.ReactNode }) {
  return (
    <Value.Composition
      className={style['AdditionalSettingsContainer']}
      help={additionalSettingsHelpContent}
      label="Additional Settings"
    >
      <Table outline>
        <thead>
          <Tr>
            <Th>Environment</Th>
            <Th>Access available</Th>
            <Th>Approval type</Th>
          </Tr>
        </thead>
        <tbody>{children}</tbody>
      </Table>
    </Value.Composition>
  );
}

export function AccessManagementSection() {
  const { api, isTeamAdmin } = useOutletContext<ApiProviderPageOutletContext>();
  const {
    data: accessManagement,
    isLoading,
    mutate,
  } = useBackend('/apis/{apiSlug}/access-management', {
    params: { path: { apiSlug: api.slug } },
  });

  const { data: formData } = Form.useData('access-management-form');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const switchContainerModeRef = useRef<SwitchContainerMode>(undefined);

  const handleSave = useCallback(
    async (data: ApiAccessManagementRequest) => {
      const response = await updateAccessManagement(api.slug, data);
      if (response.error || response.response?.status === 500) {
        setErrorMessage(response.error?.detail || 'Failed to update access request settings');
      } else {
        setErrorMessage(null);
        void mutate(data, false);
        switchContainerModeRef.current?.('view');
      }
    },
    [api.slug, mutate],
  );

  const { update } = Form.setData('access-management-form');

  const handleAttachableChange = (attachable: boolean) => {
    if (!attachable) {
      update('/availableInLiveMode', false);
    }
  };

  const hasAccessManagementChanged = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(accessManagement);
  }, [accessManagement, formData]);

  const accessRequestsEnabled = accessManagement?.attachable;

  const isAttachableWithMissingServiceNowInfo = useMemo(() => {
    const hasRequiredServiceNowInfo = hasRequiredServiceNowInformation(api);
    return accessManagement?.attachable && !hasRequiredServiceNowInfo;
  }, [accessManagement?.attachable, api]);

  const renderTableRows = useMemo(
    () => ({
      view: (
        <>
          <Tr>
            <Td>Test</Td>
            <Td>Yes</Td>
            <Td>{accessManagement?.approvalStage === 'LIVE' ? ApprovalType.AUTOMATIC : ApprovalType.SERVICE_NOW}</Td>
          </Tr>
          <Tr>
            <Td>Live</Td>
            <Td>
              <Value.Boolean path="/availableInLiveMode" />
            </Td>
            <Td>{ApprovalType.SERVICE_NOW}</Td>
          </Tr>
        </>
      ),
      edit: (
        <>
          <Tr>
            <Td>Test</Td>
            <Td>
              <Flex.Horizontal gap="x-small">
                <Field.Boolean disabled label="Test Access Available" value />
                <HelpButton size="small" title="More info">
                  <P>Test access is by default available for all apps. It is not possible to disable test access.</P>
                </HelpButton>
              </Flex.Horizontal>
            </Td>
            <Td>
              <Field.Selection
                data={[
                  { value: 'LIVE', title: ApprovalType.AUTOMATIC },
                  { value: 'TEST', title: ApprovalType.SERVICE_NOW },
                ]}
                path="/approvalStage"
                variant="radio"
              />
            </Td>
          </Tr>
          <Tr>
            <Td>Live</Td>
            <Td>
              <Field.Boolean label="Live Access Available" path="/availableInLiveMode" />
            </Td>
            <Td>
              <Flex.Horizontal gap="x-small">
                <Field.Selection
                  data={[{ value: 'TEST', title: ApprovalType.SERVICE_NOW }]}
                  disabled
                  value="TEST"
                  variant="radio"
                />
                <HelpButton size="small" title="More info">
                  <P>
                    Live access requests require approval via ServiceNow. This is the default setting and cannot be
                    modified.
                  </P>
                </HelpButton>
              </Flex.Horizontal>
            </Td>
          </Tr>
        </>
      ),
    }),
    [accessManagement],
  );

  const filteredAccessManagement = accessManagement
    ? {
        ...accessManagement,
        approvalStage:
          accessManagement.approvalStage === 'TEST' || accessManagement.approvalStage === 'LIVE'
            ? accessManagement.approvalStage
            : 'TEST',
      }
    : accessManagement;

  return (
    <Skeleton show={isLoading}>
      {filteredAccessManagement && (
        <Form.Handler
          defaultData={filteredAccessManagement}
          id="access-management-form"
          onSubmit={(data) => handleSave(data)}
        >
          <Form.Section>
            <Form.Section.ViewContainer
              onEdit={() => {
                setErrorMessage(null);
              }}
            >
              <Form.MainHeading>Access Requests</Form.MainHeading>
              <Section
                className={style['AccessManagementEnabledContainer']}
                innerSpace={{ top: 'small', bottom: 'small' }}
                top="small"
                variant={isAttachableWithMissingServiceNowInfo ? 'error' : accessRequestsEnabled ? 'info' : 'warning'}
              >
                <Flex.Horizontal gap="x-small">
                  {isAttachableWithMissingServiceNowInfo ? (
                    <Icon color="red" icon={exclamation_triangle} size="medium" />
                  ) : (
                    <IconPrimary
                      color={accessRequestsEnabled ? 'green' : 'red'}
                      icon={accessRequestsEnabled ? 'check' : 'close'}
                      size="medium"
                    />
                  )}
                  <P weight="medium">{accessRequestsEnabled ? 'Enabled' : 'Disabled'}</P>
                </Flex.Horizontal>
                <Div top="x-small">
                  {isAttachableWithMissingServiceNowInfo ? (
                    <P>
                      ServiceNow information is missing. Requesting access to this {apiOrEvent(api.type)} will not be
                      possible.
                    </P>
                  ) : accessRequestsEnabled ? (
                    <>
                      <P>Apps can request access to this {apiOrEvent(api.type)}.</P>
                      <P top="x-small">
                        Who can see and request access is controlled by <b>Visibility Settings</b>.
                      </P>
                    </>
                  ) : (
                    <P>Apps will not be able to request access to this {apiOrEvent(api.type)}.</P>
                  )}
                </Div>
              </Section>
              <Form.Visibility pathTrue="/attachable">
                <AccessManagementSettingsTable>{renderTableRows.view}</AccessManagementSettingsTable>
              </Form.Visibility>

              <Form.Section.Toolbar>
                <FormEditButton
                  disabled={!isTeamAdmin}
                  disabledTooltip={!isTeamAdmin && <>Only team admins have access to edit</>}
                  onEdit={() => setErrorMessage(null)}
                  switchContainerModeRef={switchContainerModeRef}
                />
              </Form.Section.Toolbar>
            </Form.Section.ViewContainer>
            <Form.Section.EditContainer>
              <Form.MainHeading>Access Requests</Form.MainHeading>
              <Div top="medium">
                <Field.Boolean
                  label="Enable Access Requests"
                  onChange={(value) => handleAttachableChange(!!value)}
                  path="/attachable"
                />
              </Div>
              <Form.Visibility pathTrue="/attachable">
                <AccessManagementSettingsTable>{renderTableRows.edit}</AccessManagementSettingsTable>
              </Form.Visibility>
              <Form.Section.Toolbar>
                <FormSubmitButton
                  disabled={!hasAccessManagementChanged}
                  switchContainerModeRef={switchContainerModeRef}
                />
                <Form.Section.EditContainer.CancelButton showConfirmDialog={false} />
              </Form.Section.Toolbar>
              {errorMessage && <FormStatus state="error" text={errorMessage} />}
            </Form.Section.EditContainer>
          </Form.Section>
        </Form.Handler>
      )}
    </Skeleton>
  );
}
